// Wording for the driving report (spec 004), mirroring the apps' shared
// DrivingText so every screen says the same thing, from i18n/common
// (spec 013 FR-010).
import type { Behavior, DrivingKind, DrivingSummary } from '../api/types';
import { decimal, integer, t } from '../i18n/index.ts';

export const KINDS: DrivingKind[] = ['speeding', 'hard_brake', 'rapid_accel', 'phone_use'];

const icons: Record<DrivingKind, string> = {
  speeding: 'la-tachometer-alt',
  hard_brake: 'la-hand-paper',
  rapid_accel: 'la-rocket',
  phone_use: 'la-mobile-alt',
};

export function kindLabel(k: DrivingKind): string {
  return t('drivingKind', { kind: k });
}

export function kindIcon(k: DrivingKind): string {
  return icons[k] ?? 'la-car';
}

export interface WeekLine {
  kind: DrivingKind;
  label: string;
  count: number | null;
  note: string | null;
}

// compare says how a count differs from last week's.
export function compare(now: number, last: number): string {
  if (now === last) return t('drivingSame');
  return now < last ? t('drivingFewer', { n: last - now }) : t('drivingMore', { n: now - last });
}

// week words each kind's weekly count (004 FR-048): compared with last week only
// when both weeks measured it on every drive, the drives it covers when only
// some did, and not recorded when none did.
export function week(b: Behavior): WeekLine[] {
  if (KINDS.every((k) => (b.measured[k]?.of ?? 0) === 0)) return [];
  return KINDS.map((k) => {
    const m = b.measured[k] ?? { drives: 0, of: 0 };
    const label = kindLabel(k);
    if (m.drives === 0) return { kind: k, label, count: null, note: t('drivingNotRecorded') };
    const count = b.this_week[k] ?? 0;
    const lm = b.last_measured[k] ?? { drives: 0, of: 0 };
    let note: string | null = null;
    if (m.drives < m.of) note = t('drivingCovers', { drives: m.drives, of: m.of });
    else if (lm.of > 0 && lm.drives === lm.of) note = compare(count, b.last_week[k] ?? 0);
    return { kind: k, label, count, note };
  });
}

export interface TripLine {
  counts: string | null;
  notes: string[];
}

// trip words a drive's card (004 FR-049): Passenger, the kinds that happened,
// and notes for kinds not recorded or still being checked.
export function trip(s: DrivingSummary): TripLine {
  if (s.passenger) return { counts: t('drivingPassenger'), notes: [] };
  const parts: string[] = [];
  for (const k of KINDS) {
    const n = s.counts[k] ?? 0;
    if (n > 0 && s.measured[k]?.ok !== false) parts.push(t('drivingTripCount', { kind: k, n }));
  }
  const notes: string[] = [];
  if (!s.waiting) {
    const missing = KINDS.filter((k) => s.measured[k]?.ok === false);
    if (missing.length === KINDS.length) notes.push(reason(s.measured[KINDS[0]]?.reason));
    else for (const k of missing) notes.push(t('drivingKindNotRecorded', { kind: k }));
  }
  if (s.speeding_pending) notes.push(t('drivingCheckingSpeeding'));
  return { counts: parts.length ? parts.join(' · ') : null, notes };
}

export function reason(r?: string): string {
  return r === 'report_off' ? t('drivingReportOff') : t('drivingEventsNotRecorded');
}

// speeding words a speeding stretch: "52 mph in a 35 zone" or "Over 80 mph, top 86 mph".
export function speedingText(maxMps: number, limitMps: number | undefined, miles: boolean): string {
  const speed = (mps: number) => integer(Math.round(miles ? mps / 0.44704 : mps * 3.6));
  if (limitMps != null) {
    return miles
      ? t('drivingSpeedingZoneMph', { speed: speed(maxMps), limit: speed(limitMps) })
      : t('drivingSpeedingZoneKmh', { speed: speed(maxMps), limit: speed(limitMps) });
  }
  return miles
    ? t('drivingSpeedingOverMph', { limit: integer(80), speed: speed(maxMps) })
    : t('drivingSpeedingOverKmh', { limit: integer(129), speed: speed(maxMps) });
}

// strength words a braking or acceleration peak in the viewer's units.
export function strength(mps2: number, miles: boolean): string {
  const v = miles ? mps2 / 0.44704 : mps2 * 3.6;
  // One decimal always, "8.0", cut rather than rounded, as the apps word it.
  const text = decimal(Math.floor(v * 10) / 10, 1);
  return miles ? t('drivingStrengthMph', { strength: text }) : t('drivingStrengthKmh', { strength: text });
}
